import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircleQuestion } from "lucide-react";
import { HeaderPortal } from "@/components/src/header-portal/header-portal.component";
import { useCentralStore } from "../../store/Central";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { useConnectStore } from "../../store/Connect";
import { useJobStore } from "../../store/Jobs";
import { InboxWidget } from "@/components/src/inbox/inbox-widget.component";
import {
	DeliveriesInboxWidget,
	ActiveDelivery,
} from "@/components/src/deliveries-inbox/deliveries-inbox-widget.component";
import {
	UpcomingJobsWidget,
	UpcomingJob,
} from "@/components/src/upcoming-jobs/upcoming-jobs-widget.component";
import {
	UntouchedJobsWidget,
	UntouchedJob,
} from "@/components/src/untouched-jobs/untouched-jobs-widget.component";
import { RecentFilesWidget } from "@/components/src/recent-files/recent-files-widget.component";
import { FileViewerModal } from "@/components/src/file-viewer/file-viewer-modal/file-viewer-modal.component";
import { RecentsStrip } from "@/components/src/recents-strip/recents-strip.component";
import { supabase } from "@/lib/supabase";
import { DeliveryStatusEnum } from "@/lib/types/delivery.types";
import { RolesEnum } from "@/lib/types/roles.types";
import { JobStatusEnum } from "@/lib/types/job";
import { JobDocumentTypeEnum } from "../job-page/job-document/job-document.types";
import { getRecents, getRecentFiles } from "@/lib/utils/recents";
import { getGreeting } from "@/lib/utils/greeting";
import { pageToIcon } from "../../layout/layout-components/app-bar/app-bar-components/app-bar-lists/page-to-icon";
import { SubscriptionBanner } from "../../layout/layout-components/app-bar/app-bar-components/subscription-banner.component";
import { PasswordBanner } from "../../layout/layout-components/app-bar/app-bar-components/password-banner.component";
import { DesktopUpdateBanner } from "../../layout/layout-components/app-bar/app-bar-components/desktop-update-banner.component";
import { QuickstartDashboardBanner } from "../../components/subscription/quickstart-dashboard-banner.component";
import { isConfigTrue } from "../organization/subpages/administration/konfigurationseinstellungen.page";

export const FullDashboardPage: React.FC = () => {
	const navigate = useNavigate();
	const [activeDeliveries, setActiveDeliveries] = useState<ActiveDelivery[]>(
		[]
	);
	const [upcomingJobs, setUpcomingJobs] = useState<UpcomingJob[]>([]);
	const [untouchedJobs, setUntouchedJobs] = useState<UntouchedJob[]>([]);
	const [selectedFileId, setSelectedFileId] = useState<string | null>(null);

	const profile = useCentralStore((s) => s.profile);
	const organization = useCentralStore((s) => s.organization);
	const role = useCentralStore((s) => s.role ?? RolesEnum.MEMBER);
	const clientsLookup = useCentralStore((s) => s.clientsLookup);

	const latestChannels = useRealtimeStore((s) => s.latestChannels);
	const getNotificationNavigationPath = useRealtimeStore(
		(s) => s.getNotificationNavigationPath
	);
	const updateChannelReadState = useRealtimeStore(
		(s) => s.updateChannelReadState
	);
	const profilesForChat = useRealtimeStore((s) => s.profilesForChat);

	const brandsByOrgId = useConnectStore((s) => s.brandsByOrgId);

	const jobRequestChannels = useJobStore((s) => s.jobRequestChannels);
	const fetchJobRequestChannels = useJobStore(
		(s) => s.fetchJobRequestChannels
	);

	useEffect(() => {
		fetchJobRequestChannels();
		fetchActiveDeliveries();
		fetchUpcomingJobs();
		fetchUntouchedJobs();
	}, []);

	const fetchActiveDeliveries = async () => {
		if (!organization?.id) return;
		const { data } = await supabase
			.from("deliveries")
			.select("*, jobs!inner(code, title, client_id)")
			.eq("organization_id", organization.id)
			.in("status", [
				DeliveryStatusEnum.PENDING,
				DeliveryStatusEnum.IN_TRANSIT,
			])
			.order("scheduled_departure_at", {
				ascending: true,
				nullsFirst: false,
			});

		if (data) {
			setActiveDeliveries(
				data.map((d: any) => ({
					delivery_id: d.id,
					job_id: d.job_id,
					job_code: d.jobs?.code ?? null,
					job_title: d.jobs?.title ?? null,
					status: d.status,
					courier_type: d.courier_type,
					courier_company_name: d.courier_company_name,
					description: d.description,
					scheduled_departure_at: d.scheduled_departure_at,
					created_at: d.created_at,
					client_id: d.jobs?.client_id ?? null,
				}))
			);
		}
	};

	const fetchUpcomingJobs = async () => {
		if (!organization?.id) return;
		const now = new Date();
		const past24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
		const plus7d = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

		const { data } = await supabase
			.from("jobs_with_shares")
			.select(
				"id, code, title, status, outgoing_order_date, client_id, patients(first_name, last_name)"
			)
			.eq("organization_id", organization.id)
			.neq("status", JobStatusEnum.ARCHIVED)
			.gte("outgoing_order_date", past24h.toISOString().split("T")[0])
			.lte("outgoing_order_date", plus7d.toISOString().split("T")[0])
			.order("outgoing_order_date", { ascending: true });

		if (data) {
			setUpcomingJobs(
				data.map((j: any) => {
					const client = clientsLookup[j.client_id];
					return {
						id: j.id,
						code: j.code,
						title: j.title,
						status: j.status,
						outgoing_order_date: j.outgoing_order_date,
						client_id: j.client_id,
						client_name: client?.company_name ?? "",
						patient_first_name: j.patients?.first_name ?? null,
						patient_last_name: j.patients?.last_name ?? null,
					};
				})
			);
		}
	};

	const fetchUntouchedJobs = async () => {
		if (!organization?.id) return;

		const { data } = await supabase
			.from("jobs_with_shares")
			.select(
				"id, code, title, client_id, channel_id, patients(first_name, last_name), job_documents(id, type, deleted_at, job_items(count))"
			)
			.eq("organization_id", organization.id)
			.eq("status", JobStatusEnum.IN_PROGRESS)
			.order("created_at", { ascending: true });

		if (!data) return;

		const billableDocumentTypes = [
			JobDocumentTypeEnum.Quotation,
			JobDocumentTypeEnum.DeliveryNote,
			JobDocumentTypeEnum.CreditNote,
		];

		const requireNoDocuments = isConfigTrue(organization?.config, "untouched_jobs_require_no_documents");

		const empty = data.filter((j: any) => {
			const docs = (j.job_documents ?? []).filter(
				(d: any) => d.deleted_at === null
			);
			if (requireNoDocuments) return docs.length === 0;
			for (const doc of docs) {
				if (!billableDocumentTypes.includes(doc.type)) continue;
				const itemCount = doc.job_items?.[0]?.count ?? 0;
				if (itemCount > 0) return false;
			}
			return true;
		});

		setUntouchedJobs(
			empty.map((j: any) => {
				const client = clientsLookup[j.client_id];
				return {
					id: j.id,
					code: j.code,
					title: j.title,
					client_id: j.client_id,
					client_name: client?.company_name ?? "",
					patient_first_name: j.patients?.first_name ?? null,
					patient_last_name: j.patients?.last_name ?? null,
					from_request: !!j.channel_id,
				};
			})
		);
	};

	const recents = getRecents().filter(
		(item) => !(item.type === "page" && item.key === "dashboard-home")
	);
	const recentFiles = getRecentFiles();

	const getOrgName = (orgId: string): string | undefined => {
		const brand = brandsByOrgId[orgId];
		if (brand?.name) return brand.name;
		const client = Object.values(clientsLookup).find(
			(c) => c.connected_organization_id === orgId
		);
		return client?.company_name || undefined;
	};

	const handleDeliveryClick = (delivery: ActiveDelivery) => {
		if (delivery.client_id && delivery.job_id) {
			navigate(`/client/${delivery.client_id}/job/${delivery.job_id}`);
		}
	};

	const handleJobClick = (job: UpcomingJob) => {
		navigate(`/client/${job.client_id}/job/${job.id}`);
	};

	const handleUntouchedJobClick = (job: UntouchedJob) => {
		navigate(`/client/${job.client_id}/job/${job.id}`);
	};

	const firstName = profile?.first_name || "";

	return (
		<div className="w-full h-full flex flex-col overflow-hidden">
			<HeaderPortal>
				<div className="flex items-center gap-3">
					<h2 className="text-lg text-gray-800">
						{getGreeting()}
						{firstName ? `, ${firstName}` : ""}.
					</h2>
					<span className="text-sm text-gray-400">
						{new Date().toLocaleDateString("de-CH", {
							weekday: "long",
							day: "numeric",
							month: "long",
							year: "numeric",
						})}
					</span>
				</div>
			</HeaderPortal>

			<RecentsStrip
				items={recents}
				pageIcon={(key) =>
					pageToIcon(key, {
						width: "16px",
						height: "16px",
						color: "rgb(100, 116, 139)",
					})
				}
			/>

			<div className="px-3 pt-3 flex flex-wrap gap-2">
				<SubscriptionBanner expanded={true} />
				<PasswordBanner expanded={true} />
				<DesktopUpdateBanner expanded={true} />
				<QuickstartDashboardBanner />
			</div>

			<div className="flex-1 min-h-0 p-3 grid grid-cols-1 lg:grid-cols-3 lg:grid-rows-1 gap-3">
				{/* Column 1: Inbox (top half) + Untouched jobs (bottom half) */}
				<div className="min-h-0 flex flex-col gap-3">
					<InboxWidget
						channels={latestChannels}
						getNavigationPath={
							getNotificationNavigationPath ??
							((ch) => `/chats/${ch.id}`)
						}
						onMarkAsRead={updateChannelReadState}
						organizationId={organization?.id ?? ""}
						getOrgName={getOrgName}
						userId={profile?.id ?? ""}
						profilesForChat={profilesForChat}
						className="flex-1 min-h-0"
						showJobRequestsFilter={true}
						jobRequestChannels={jobRequestChannels}
					/>

					<UntouchedJobsWidget
						jobs={untouchedJobs}
						onJobClick={handleUntouchedJobClick}
						className="flex-1 min-h-0"
					/>
				</div>

				{/* Column 2: Upcoming Jobs */}
				<UpcomingJobsWidget
					jobs={upcomingJobs}
					onJobClick={handleJobClick}
					agendaLink="/agenda"
					className="min-h-0"
				/>

				{/* Column 3: Deliveries + Recent Files + Support */}
				<div className="min-h-0 flex flex-col gap-3">
					<DeliveriesInboxWidget
						deliveries={activeDeliveries}
						onDeliveryClick={handleDeliveryClick}
						deliveriesPageLink="/lieferungen"
						className="flex-1 min-h-0"
					/>

					<RecentFilesWidget
						files={recentFiles}
						onOpenFile={(fileId) => setSelectedFileId(fileId)}
						onNavigateToJob={(clientId, jobId) =>
							navigate(`/client/${clientId}/job/${jobId}`)
						}
						className="flex-1 min-h-0"
					/>

					<div
						className="flex items-center gap-2.5 p-3 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 cursor-pointer transition-colors flex-shrink-0"
						onClick={() => window.Intercom?.("show")}
						data-testid="dashboard-support-link"
						aria-label="Support kontaktieren"
					>
						<MessageCircleQuestion className="h-4 w-4 text-gray-400" />
						<span className="text-sm text-gray-600">Support</span>
					</div>
				</div>
			</div>

			{selectedFileId && (
				<FileViewerModal
					fileIds={[selectedFileId]}
					isOpen={!!selectedFileId}
					onClose={() => setSelectedFileId(null)}
					metadata={recentFiles.find(
						(f) => f.fileId === selectedFileId
					)}
				/>
			)}
		</div>
	);
};
